import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  Image,
  InputGroup,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/tablet-login-concept-illustration_114360-7963.avif';
import { useAdminLogin } from './hooks/useAdminLogin';

function SignIn() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [userIdError, setUserIdError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const textPrimary = useColorModeValue('gray.600', 'gray.300');
  const textSecondary = useColorModeValue('gray.500', 'gray.300');

  const { mutate: loginAdmin, isPending } = useAdminLogin();

  const handleLogin = (e) => {
    e.preventDefault();

    let valid = true;

    if (!userId) {
      setUserIdError('User ID is required');
      valid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      valid = false;
    }

    if (!valid) return;

    loginAdmin(
      { userId, password },
      {
        onSuccess: () => {
          navigate('/admin/protocol');
        },
      },
    );
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      {isPending && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="blackAlpha.600"
          zIndex="9999"
          align="center"
          justify="center"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Flex>
      )}
      <Flex align="center" justify="flex-start" h="100vh" w="100%">
        <Box
          bg={cardBg}
          p={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="lg"
          w={{ base: '100%', md: '400px' }}
        >
          <Image
            src="/logo.png"
            alt="Zudpi Land Survey Logo"
            mx="auto"
            maxH="120px"
            p="10px"
          />

          <Text fontSize="sm" color={textSecondary} mb={6} textAlign="center">
            Login using your User ID and Password
          </Text>

          <form onSubmit={handleLogin}>
            {/* USER ID */}
            <FormControl mb={4} isInvalid={!!userIdError}>
              <FormLabel fontSize="sm" color={textPrimary}>
                User ID
              </FormLabel>
              <Input
                placeholder="Enter User ID"
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setUserIdError('');
                }}
              />
              {userIdError && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {userIdError}
                </Text>
              )}
            </FormControl>

            {/* PASSWORD */}
            <FormControl mb={6} isInvalid={!!passwordError}>
              <FormLabel fontSize="sm" color={textPrimary}>
                Password
              </FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {passwordError && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {passwordError}
                </Text>
              )}
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              w="100%"
              mb={4}
              isPending={isPending}
              loadingText="Logging in"
            >
              Login
            </Button>
            <Button
              variant="ghost"
              colorScheme="blue"
              w="100%"
              onClick={() => navigate('/auth/reset-password')}
            >
              Reset Password
            </Button>
          </form>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
